import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      nav("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          <img src="/icon-192.png" alt="WorkHub" className="h-10 w-10 rounded-lg" />
          <span className="text-xl font-bold font-display">WorkHub</span>
        </div>
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold font-display leading-tight">İşinizi tek platformda yönetin.</h2>
          <p className="text-slate-300 mt-4 max-w-md">Görevler, projeler, takvim, zaman takibi, talepler ve yönetim raporları — hepsi WorkHub'da, Türkçe ve mobil uyumlu.</p>
        </div>
        <div className="text-slate-400 text-sm relative z-10">© 2026 WorkHub</div>
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-indigo-600/30 blur-3xl" />
        <div className="absolute -left-24 -bottom-24 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/icon-192.png" alt="WorkHub" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold font-display">WorkHub</span>
          </div>
          <h1 className="text-2xl font-bold font-display">Tekrar hoş geldiniz</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Hesabınıza giriş yapın</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Şifre</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Şifremi unuttum</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5" />
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Giriş Yap
            </Button>
          </form>
          <p className="text-sm text-center mt-4 text-muted-foreground">
            Hesabınız yok mu? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">Kayıt olun</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
